import React from "react";
import { Box, Button, Heading, Text, VStack } from "@chakra-ui/react";
import i18n from "../utils/i18n";

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string;
}

/**
 * アプリケーション全体の予期しないレンダリングエラーをキャッチするError Boundary
 * エラー発生時にユーザーにエラー画面を表示し、復帰手段を提供する
 */
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error(
      "予期しないエラーが発生しました:",
      error.message,
      errorInfo.componentStack
    );
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <Box
          w="100vw"
          h="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="gray.50"
        >
          <VStack spacing={6} textAlign="center" p={8}>
            <Heading size="lg" color="gray.700">
              {i18n.t('common.errorBoundary.title')}
            </Heading>
            <Text color="gray.500">
              {i18n.t('common.errorBoundary.message')}
            </Text>
            <Text fontSize="sm" color="gray.400">
              {this.state.errorMessage}
            </Text>
            <Button colorScheme="blue" onClick={this.handleReload}>
              {i18n.t('common.errorBoundary.reload')}
            </Button>
          </VStack>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
